import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, Slider, Chip, Grid, IconButton } from "@mui/material";
import axios from "axios";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";

const WeeklyCoshhProductCheck = ({ onClose, setOpenDefaultSnackbar, selectedTask }) => {
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [amount, setAmount] = useState(50);
  const [comments, setComments] = useState("");
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  useEffect(() => {
    const fetchFilteredData = async () => {
      setLoading(true);
      setFilteredData(null);

      try {
        // Get the date 7 days ago in YYYY-MM-DD format
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const formattedSevenDaysAgo = sevenDaysAgo.toISOString().split("T")[0];

        // API request to filter tasks created within the last 7 days
        const response = await axios.get("/api/product/productCheck/filter", {
          params: {
            start_date: formattedSevenDaysAgo, // Filter start date
            end_date: currentDate, // Filter end date (current date)
          },
        });

        // Set fetched data
        setFilteredData(response.data.data);
      } catch (err) {
        console.error("Error fetching filtered data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredData();
  }, [currentDate]);

  // Corrected extraction of products
  const products = filteredData ? filteredData.flatMap((entry) => entry.products.map((item) => item.product)) : [];

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        product: products[currentProductIndex],
        quantity,
        amount: `${amount}%`,
        comments,
        date: selectedDate,
      },
    ]);
    setQuantity(1);
    setAmount(50);
    setComments("");

    if (currentProductIndex < products.length - 1) {
      setCurrentProductIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const filteredDataId = filteredData?.length ? filteredData[0].id : null;

  const handleSubmit = async () => {
    const payload = {
      products: responses,
      completed_by: user.username,
    };

    try {
      await axios.put(`/api/product/productCheck/${filteredDataId}`, payload);

      if (taskId) {
        updateSubTask(taskId); // Use the stored task ID
      }

      setResponses([]);
      setQuantity(1);
      setAmount(50);
      setComments("");
      setSelectedDate(currentDate);
      onClose();
    } catch (error) {
      console.error("Error submitting responses:", error);
      alert("Failed to submit responses. Please try again.");
    }
  };

  const handleQuantityChange = (change) => {
    setQuantity((prev) => Math.max(0, prev + change));
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>
        Weekly COSHH Product Check
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {currentDate} &nbsp; • &nbsp; <span style={{ color: "#1751D0" }}>Kindergarten Bedroom</span>
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", fontSize: "14px", mb: 2 }}>
        <Grid container>
          <Grid item xs={5} sx={{ color: "#1751D0" }}>
            Product {currentProductIndex + 1} of {products.length} Complete!
          </Grid>
          <Grid item xs={4}>
            <span style={{ color: "#1751D0" }}>{currentProductIndex + 1} </span>
            {`/ ${products.length}`}
          </Grid>
        </Grid>
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Box
            sx={{
              maxHeight: "500px", // or any height you need
              overflowY: "auto",
              pr: 1, // optional: padding for scrollbar spacing
            }}
          >
            {products.map((product, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #E0E0E0",
                  py: 1,
                }}
              >
                <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>{`${index + 1} ${product}`}</Typography>
                <IconButton
                  color='primary'
                  sx={{
                    color: responses.find((response) => response.product === product) ? "#1751D0" : "#E0E0E0",
                  }}
                >
                  <CheckCircleIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Grid container mb={2}>
            <Grid item lg={6} md={12}>
              <Typography sx={{ fontSize: "14px", mb: 2 }}>
                Product: <br />
                {products[currentProductIndex]}
              </Typography>
            </Grid>
            <Grid item lg={6} md={12}>
              <Button
                variant='outlined'
                startIcon={<ShoppingCartOutlinedIcon />}
                sx={{
                  borderRadius: "50px", // Rounded edges
                  borderColor: "#2979ff", // Border color (blue)
                  color: "#4a4a4a", // Text color
                  fontWeight: "50",
                  textTransform: "none", // Prevent uppercase text
                  padding: "8px 20px",
                  fontSize: "12px",
                  "&:hover": {
                    borderColor: "#1a73e8",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Add to shopping list
              </Button>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "14px", mb: 2 }}>Quantity</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Button variant='outlined' color='primary' onClick={() => handleQuantityChange(-1)}>
              -
            </Button>
            <Typography>{quantity}</Typography>
            <Button variant='outlined' color='primary' onClick={() => handleQuantityChange(1)}>
              +
            </Button>
          </Box>

          <Typography sx={{ fontSize: "14px", mb: 2 }}>Amount</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography>{amount}%</Typography>
            <Slider value={amount} onChange={(e, value) => setAmount(value)} min={0} max={100} step={1} />
          </Box>

          <Typography sx={{ fontSize: "14px", mt: 2, mb: 2 }}>Comments</Typography>
          <TextField fullWidth multiline rows={3} value={comments} onChange={(e) => setComments(e.target.value)} />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,

          zIndex: 10,
        }}
      >
        {currentProductIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {currentProductIndex > 0 && (
          <Button variant='outlined' color='primary' onClick={() => setCurrentProductIndex((prev) => prev - 1)}>
            Previous
          </Button>
        )}
        {currentProductIndex < products.length - 1 ? (
          <Button variant='contained' color='primary' onClick={handleNext} disabled={quantity === 0}>
            Next
          </Button>
        ) : (
          <Button variant='contained' color='primary' onClick={handleSubmit} disabled={quantity === 0}>
            Submit
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WeeklyCoshhProductCheck;
